<template>
  <!--
    DocumentCategory.vue - En kategori med dokumenter
    ================================================
    Viser dokumenter i en kategori med drag-and-drop support.

    Props:
    - category: Kategori nøgle ('grundlag', 'kreditnoter', etc.)
    - title: Visningsnavn
    - icon: Feather icon navn
    - variant: Bootstrap variant farve
    - documents: Array af dokumenter
    - required: Om mindst ét dokument er påkrævet
    - isTestMode: Om vi er i test mode (manuel analyse)
  -->
  <div
    class="document-category"
    :class="`category-${variant}`"
  >
    <!-- Category Header -->
    <div class="category-header">
      <div class="category-title">
        <feather-icon
          :icon="icon"
          size="20"
          class="mr-50"
        />
        <h5 class="mb-0">
          {{ title }}
          <b-badge
            v-if="documents.length > 0"
            :variant="variant"
            pill
            class="ml-50"
          >
            {{ documents.length }}
          </b-badge>
          <b-badge
            v-if="required"
            variant="light-danger"
            class="ml-50"
          >
            {{ $t('DEBTIA_DOC_REQUIRED') }}
          </b-badge>
        </h5>
      </div>
    </div>

    <!-- Documents List with Draggable -->
    <draggable
      v-model="localDocuments"
      :group="{ name: 'documents', pull: true, put: true }"
      class="documents-list"
      :class="{ 'empty-category': documents.length === 0 }"
      handle=".drag-handle"
      ghost-class="ghost"
      @change="handleDragChange"
    >
      <div
        v-for="doc in localDocuments"
        :key="doc.id"
        class="document-item"
        :class="{
          'document-valid': doc.isValid,
          'document-invalid': doc.file && !doc.isValid,
          'document-ai-processed': doc.aiAnalyzed,
          'document-just-added': doc.id === recentlyAddedId,
        }"
      >
        <!-- Drag Handle -->
        <div
          class="drag-handle"
          title="Træk for at flytte til en anden kategori"
        >
          <feather-icon
            icon="MoreVerticalIcon"
            size="16"
          />
          <feather-icon
            icon="MoreVerticalIcon"
            size="16"
            class="ml-n2"
          />
        </div>

        <!-- Document Info -->
        <div
          class="document-info"
          @click="$emit('edit', doc.id)"
        >
          <div class="document-icon">
            <feather-icon
              :icon="getFileIcon(doc.file)"
              size="24"
              :class="getFileIconClass(doc.file)"
            />
          </div>
          <div class="document-details">
            <div class="document-name">
              {{ doc.title || (doc.file && doc.file.name) || $t('DEBTIA_COMMON_UNNAMED') }}
            </div>
            <div class="document-meta">
              <span v-if="doc.file">
                {{ getFileExtension(doc.file).toUpperCase() }}
                &middot;
                {{ formatFileSize(doc.file.size) }}
              </span>
              <!-- Rykkere: vis gebyr, renter, kompensation -->
              <template v-if="category === 'rykkere'">
                <span
                  v-if="doc.reminderFee"
                  class="ml-1"
                >
                  &middot; Gebyr {{ formatAmount(doc.reminderFee) }} kr
                </span>
                <span
                  v-if="doc.interestAmount"
                  class="ml-1"
                >
                  &middot; Renter {{ formatAmount(doc.interestAmount) }} kr
                </span>
                <span
                  v-if="doc.hasCompensationFee"
                  class="ml-1"
                >
                  &middot; Kompensation 310 kr
                </span>
              </template>
              <!-- Grundlag: vis beløb + rentesats -->
              <template v-else-if="category === 'grundlag'">
                <span
                  v-if="doc.paymentAmount"
                  class="ml-1"
                >
                  &middot; {{ formatAmount(doc.paymentAmount) }} kr
                </span>
                <span
                  v-if="doc.interestRate"
                  class="ml-1"
                >
                  &middot; Rente {{ doc.interestRate }}%
                </span>
              </template>
              <!-- Andre kategorier: vis beløb hvis det findes -->
              <template v-else>
                <span
                  v-if="doc.paymentAmount"
                  class="ml-1"
                >
                  &middot; {{ formatAmount(doc.paymentAmount) }} kr
                </span>
              </template>
            </div>
          </div>
        </div>

        <!-- AI Badge & Actions -->
        <div class="document-actions">
          <!-- AI Confidence Badge -->
          <b-badge
            v-if="doc.aiAnalyzed"
            :variant="getConfidenceVariant(doc.aiConfidence)"
            class="mr-50"
          >
            <feather-icon
              icon="CpuIcon"
              size="12"
              class="mr-25"
            />
            {{ doc.aiConfidence }}%
          </b-badge>

          <!-- Validation Status -->
          <feather-icon
            v-if="doc.isValid"
            icon="CheckCircleIcon"
            size="18"
            class="text-success mr-50"
          />
          <feather-icon
            v-else-if="doc.file"
            icon="AlertCircleIcon"
            size="18"
            class="text-warning mr-50"
          />

          <!-- Analyze Button (test mode only) -->
          <b-button
            v-if="isTestMode && !doc.aiAnalyzed"
            variant="flat-primary"
            size="sm"
            class="btn-icon mr-25"
            :title="$t('DEBTIA_DOC_ANALYZE_AI')"
            @click.stop="$emit('analyze', doc.id)"
          >
            <feather-icon
              icon="ZapIcon"
              size="14"
            />
          </b-button>

          <!-- Edit Button -->
          <b-button
            variant="flat-primary"
            size="sm"
            class="btn-icon mr-25"
            :title="$t('DEBTIA_COMMON_EDIT')"
            @click.stop="$emit('edit', doc.id)"
          >
            <feather-icon
              icon="EditIcon"
              size="14"
            />
          </b-button>

          <!-- Remove Button -->
          <b-button
            variant="flat-danger"
            size="sm"
            class="btn-icon"
            :title="$t('DEBTIA_COMMON_REMOVE')"
            @click.stop="$emit('remove', doc.id)"
          >
            <feather-icon
              icon="TrashIcon"
              size="14"
            />
          </b-button>
        </div>
      </div>

      <!-- Empty State -->
      <div
        v-if="documents.length === 0"
        class="empty-state"
      >
        <feather-icon
          :icon="isTestMode ? 'ArrowDownIcon' : icon"
          size="32"
          class="text-muted mb-50"
        />
        <p class="text-muted mb-0">
          {{ isTestMode ? 'Træk dokumenter hertil' : 'AI placerer relevante dokumenter her' }}
        </p>
      </div>
    </draggable>
  </div>
</template>

<script>
import { BBadge, BButton } from 'bootstrap-vue'
import { computed, ref } from '@vue/composition-api'
import draggable from 'vuedraggable'

export default {
  name: 'DocumentCategory',

  components: {
    BBadge,
    BButton,
    draggable,
  },

  props: {
    category: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    icon: {
      type: String,
      default: 'FileIcon',
    },
    variant: {
      type: String,
      default: 'primary',
    },
    documents: {
      type: Array,
      default: () => [],
    },
    required: {
      type: Boolean,
      default: false,
    },
    isTestMode: {
      type: Boolean,
      default: false,
    },
    alwaysShow: {
      type: Boolean,
      default: false,
    },
  },

  setup(props, { emit }) {
    // Local copy for draggable v-model
    const localDocuments = computed({
      get: () => props.documents,
      set: () => {
        // Handled by drag events
      },
    })

    // Track recently added document for highlight animation
    const recentlyAddedId = ref(null)

    // Handle drag changes
    function handleDragChange(evt) {
      if (evt.added) {
        // Document was moved TO this category
        const doc = evt.added.element
        emit('move', doc.id, doc.category, props.category)

        // Trigger highlight animation on the newly added document
        recentlyAddedId.value = doc.id
        setTimeout(() => {
          recentlyAddedId.value = null
        }, 1200)
      }
    }

    // File helpers
    function getFileExtension(file) {
      if (!file) return ''
      return file.name.split('.').pop().toLowerCase()
    }

    function getFileIcon(file) {
      if (!file) return 'FileIcon'
      const ext = getFileExtension(file)
      if (ext === 'pdf') return 'FileTextIcon'
      if (['jpg', 'jpeg', 'png'].includes(ext)) return 'ImageIcon'
      if (['doc', 'docx'].includes(ext)) return 'FileTextIcon'
      return 'FileIcon'
    }

    function getFileIconClass(file) {
      if (!file) return ''
      const ext = getFileExtension(file)
      if (ext === 'pdf') return 'text-danger'
      if (['jpg', 'jpeg', 'png'].includes(ext)) return 'text-success'
      if (['doc', 'docx'].includes(ext)) return 'text-primary'
      return ''
    }

    function formatFileSize(bytes) {
      if (!bytes) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return `${parseFloat((bytes / k ** i).toFixed(1))} ${sizes[i]}`
    }

    function formatAmount(amount) {
      if (!amount) return '0'
      return parseFloat(amount).toLocaleString('da-DK', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    }

    function getConfidenceVariant(confidence) {
      if (confidence >= 90) return 'light-success'
      if (confidence >= 70) return 'light-warning'
      return 'light-danger'
    }

    return {
      localDocuments,
      recentlyAddedId,
      handleDragChange,
      getFileExtension,
      getFileIcon,
      getFileIconClass,
      formatFileSize,
      formatAmount,
      getConfidenceVariant,
    }
  },
}
</script>

<style lang="scss" scoped>
.document-category {
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  overflow: hidden;
  background: #fff;
}

.category-header {
  padding: 0.75rem 1rem;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;

  .category-title {
    display: flex;
    align-items: center;

    h5 {
      display: flex;
      align-items: center;
    }
  }
}

// Category variants
.category-primary {
  border-color: rgba(115, 103, 240, 0.3);

  .category-header {
    background: rgba(115, 103, 240, 0.05);
    border-color: rgba(115, 103, 240, 0.2);
  }
}

.category-info {
  border-color: rgba(0, 207, 232, 0.3);

  .category-header {
    background: rgba(0, 207, 232, 0.05);
    border-color: rgba(0, 207, 232, 0.2);
  }
}

.category-warning {
  border-color: rgba(255, 159, 67, 0.3);

  .category-header {
    background: rgba(255, 159, 67, 0.05);
    border-color: rgba(255, 159, 67, 0.2);
  }
}

.category-secondary {
  border-color: rgba(130, 134, 139, 0.3);

  .category-header {
    background: rgba(130, 134, 139, 0.05);
    border-color: rgba(130, 134, 139, 0.2);
  }
}

// Documents List
.documents-list {
  min-height: 60px;
  padding: 0.5rem;

  &.empty-category {
    min-height: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

// Document Item
.document-item {
  display: flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 0.375rem;
  margin-bottom: 0.5rem;
  transition: all 0.2s ease;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    border-color: #7367f0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }

  &.document-valid {
    border-color: #28c76f;
    background: rgba(40, 199, 111, 0.02);
  }

  &.document-invalid {
    border-color: #ff9f43;
    background: rgba(255, 159, 67, 0.02);
  }

  &.document-ai-processed {
    border-left: 3px solid #7367f0;
  }

  &.ghost {
    opacity: 0.5;
    background: #f8f9fa;
  }
}

// Drag Handle
.drag-handle {
  display: flex;
  padding: 0.25rem;
  margin-right: 0.5rem;
  cursor: grab;
  color: #b9b9c3;

  &:hover {
    color: #5e5873;
  }

  &:active {
    cursor: grabbing;
  }
}

// Document Info
.document-info {
  display: flex;
  align-items: center;
  flex: 1;
  cursor: pointer;
  overflow: hidden;
}

.document-icon {
  margin-right: 0.75rem;
  flex-shrink: 0;
}

.document-details {
  overflow: hidden;
}

.document-name {
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 250px;
}

.document-meta {
  font-size: 0.75rem;
  color: #b9b9c3;
}

// Document Actions
.document-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-left: 0.5rem;
}

// Empty State
.empty-state {
  text-align: center;
  padding: 1rem;
  width: 100%;
}

// Highlight animation for newly added documents
.document-just-added {
  animation: highlightPulse 1.2s ease-out;
}

@keyframes highlightPulse {
  0% {
    background: rgba(8, 140, 118, 0.15);
    box-shadow: 0 0 0 2px rgba(8, 140, 118, 0.3);
  }
  100% {
    background: #fff;
    box-shadow: none;
  }
}

// Responsive
@media (max-width: 576px) {
  .document-name {
    max-width: 150px;
  }

  .document-actions .btn {
    padding: 0.25rem;
  }
}

// ---- Dark Mode ----
.dark-layout {
  .document-category {
    background: #091828;
    border-color: #163350;
  }

  .category-header {
    background: #0b1e32;
    border-color: #163350;
  }

  // Category variants (dark)
  .category-primary {
    border-color: rgba(115, 103, 240, 0.25);

    .category-header {
      background: rgba(115, 103, 240, 0.08);
      border-color: rgba(115, 103, 240, 0.2);
    }
  }

  .category-info {
    border-color: rgba(0, 207, 232, 0.25);

    .category-header {
      background: rgba(0, 207, 232, 0.08);
      border-color: rgba(0, 207, 232, 0.2);
    }
  }

  .category-warning {
    border-color: rgba(255, 159, 67, 0.25);

    .category-header {
      background: rgba(255, 159, 67, 0.08);
      border-color: rgba(255, 159, 67, 0.2);
    }
  }

  .category-secondary {
    border-color: rgba(130, 134, 139, 0.2);

    .category-header {
      background: rgba(130, 134, 139, 0.06);
      border-color: rgba(130, 134, 139, 0.15);
    }
  }

  .document-item {
    background: #0b1e32;
    border-color: #163350;

    &:hover {
      border-color: #7367f0;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    }

    &.document-valid {
      border-color: rgba(40, 199, 111, 0.4);
      background: rgba(40, 199, 111, 0.05);
    }

    &.document-invalid {
      border-color: rgba(255, 159, 67, 0.4);
      background: rgba(255, 159, 67, 0.05);
    }

    &.ghost {
      background: #071521;
    }
  }

  .drag-handle {
    color: #3b5068;

    &:hover {
      color: #8a9bb5;
    }
  }

  .document-name {
    color: #e2e8f0;
  }

  .document-meta {
    color: #6b8299;
  }

  @keyframes highlightPulse {
    0% {
      background: rgba(8, 140, 118, 0.2);
      box-shadow: 0 0 0 2px rgba(8, 140, 118, 0.4);
    }
    100% {
      background: #0b1e32;
      box-shadow: none;
    }
  }
}
</style>
